import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient, PaketUebersicht } from '../api';
import { IconHerunterladen } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, ErrorState, LoadingState, PropertyGrid, StatusBadge,
} from '../muster';

/**
 * Ausgabe der Installationspakete.
 *
 * Die Konfigurationsdateien sind bereits ausgefuellt - sie entstehen beim
 * Herunterladen aus den Servereinstellungen. Von Hand eingetragene Kennungen
 * waren bisher die haeufigste Fehlerquelle bei der Einrichtung.
 */
export function Installation({ api }: { api: ApiClient }) {
  const [uebersicht, setUebersicht] = useState<PaketUebersicht | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [ladefehler, setLadefehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = async (pfad: string, name: string, kennung: string) => {
    setLadefehler(null);
    setLaeuft(kennung);
    try {
      await api.dateiLaden(pfad, name);
    } catch (e) {
      setLadefehler(e);
    } finally {
      setLaeuft(null);
    }
  };

  const uebersichtLaden = useCallback(() => {
    setFehler(null);
    api.pakete().then(setUebersicht).catch(setFehler);
  }, [api]);

  useEffect(() => { uebersichtLaden(); }, [uebersichtLaden]);

  const kopf = (
    <PageHeader
      titel="Installation"
      beschreibung={'Die Konfigurationsdateien sind bereits ausgefüllt — sie entstehen '
        + 'beim Herunterladen aus den Servereinstellungen. Von Hand eingetragene '
        + 'Kennungen waren bisher die häufigste Fehlerquelle bei der Einrichtung.'}
      meta={uebersicht && <>Server {uebersicht.serveradresse}</>}
    />
  );

  if (fehler && !uebersicht) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Installationspakete konnten nicht abgefragt werden."
          technisch={fehler}
          beiErneutVersuchen={uebersichtLaden}
        />
      </>
    );
  }

  if (!uebersicht) {
    return (
      <>
        {kopf}
        <LoadingState text="Installationspakete werden geladen …" zeilen={4} />
      </>
    );
  }

  const paket = (kennung: string) => uebersicht.pakete.find((p) => p.kennung === kennung);

  return (
    <>
      {kopf}

      {ladefehler !== null && (
        <ErrorState
          klein
          meldung="Die Datei konnte nicht heruntergeladen werden."
          technisch={ladefehler}
        />
      )}

      <ContentSection
        titel="Revit-Add-in für Arbeitsplätze"
        beschreibung={'Die Schaltfläche in Revit, mit der Planer Exporte beauftragen. '
          + 'Gehört auf jeden Arbeitsplatz, an dem exportiert wird.'}
      >

        <Fassung paket={paket('addin')} />
        <Zustand vorhanden={paket('addin')?.vorhanden ?? false} />

        {paket('addin')?.vorhanden && (
          <>
            <p>
              <button
                className="m-knopf m-knopf-haupt"
                disabled={laeuft === 'addin'}
                onClick={() => laden('/api/downloads/addin', 'TuvExportAddin.zip', 'addin')}
              >
                {laeuft === 'addin' ? 'wird geladen …' : <><IconHerunterladen size={16} />Herunterladen</>}
              </button>
            </p>

            <Schritte punkte={[
              'Auf dem Arbeitsplatz entpacken, Revit muss geschlossen sein.',
              'install-arbeitsplatz.ps1 als Administrator ausführen.',
              'Revit starten - der Reiter TuV erscheint.',
            ]} />

            <p className="sehr-schwach" style={{ maxWidth: 680 }}>
              Die Datei <code>tuv-export.settings.json</code> ist im Paket bereits
              ausgefüllt. Beim ersten Start meldet sich der Anwender mit seinem
              Firmenkonto an.
            </p>

            {/* ══════════════════════════════════════════════════════════════
                WO DIE DATEIEN LANDEN — UND WARUM DAS HIER STEHT

                Revit liest Addin-Manifeste NUR aus dem Jahresordner selbst,
                nicht aus dessen Unterordnern. Lag die .addin eine Ebene zu
                tief, blieb das alte Manifest gültig und zeigte auf eine
                ältere Programmdatei. Sichtbar wurde das erst daran, dass
                eine neue Schaltfläche nicht erschien — ohne Fehlermeldung.

                Wer von Hand nachsieht, soll die Sollablage kennen, statt
                sie aus einem Skript zu rekonstruieren. ═══════════════ */}
            <div className="hinweis" style={{ maxWidth: 680 }}>
              <strong>Wohin es installiert wird</strong>
              <pre style={{ margin: '0.4rem 0 0.6rem' }}>
{String.raw`%ProgramData%\Autodesk\Revit\Addins\2025\
   Tuv.Revit.Addin.addin      das Manifest
   TuvExport\                 die Programmdateien`}
              </pre>
              <p className="sehr-schwach" style={{ margin: 0 }}>
                Das Manifest gehört in den Jahresordner selbst — Revit liest
                Manifeste nicht aus Unterordnern. Es verweist relativ auf{' '}
                <code>TuvExport\Tuv.Revit.Addin.dll</code>. Ältere Manifeste
                derselben Anwendung entfernt das Skript, sonst lädt Revit
                weiter die alte Fassung.
              </p>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            REVIT 2027: VORABFASSUNG (Eigentümer F439, CL-2026-0059)

            Schon jetzt zu haben, aber noch nicht in echtem Revit 2027
            geprüft. Die Kennzeichnung steht deshalb am Titel, am Knopf und
            im Hinweis — wer nur Revit 2025 nutzt, bleibt beim Paket oben.
            Solange das Objekt nicht hinterlegt ist, erscheint nichts. ═══ */}
        {paket('addin-2027')?.vorhanden && (
          <div data-paket="addin-2027"
               style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid var(--linie)' }}>
            <h3 style={{ marginTop: 0, fontSize: 15 }}>
              Für Revit 2027{' '}
              <StatusBadge zustand="warnung" text="Vorabfassung" klein />
            </h3>

            <div className="hinweis warnung" style={{ maxWidth: 680 }} data-vorabfassung>
              <span className="hinweis-zeichen">!</span>
              <div>
                <strong>Vorabfassung – noch nicht in Revit 2027 praxisgeprüft.</strong>{' '}
                Nur für Arbeitsplätze mit Revit 2027. Für Revit 2025 gilt weiter
                das Paket oben. Auffälligkeiten bitte melden.
              </div>
            </div>

            <p>
              <button
                className="m-knopf"
                disabled={laeuft === 'addin-2027'}
                onClick={() => laden('/api/downloads/addin-2027', 'TuvExportAddin-2027.zip', 'addin-2027')}
              >
                {laeuft === 'addin-2027'
                  ? 'wird geladen …'
                  : <><IconHerunterladen size={16} />Vorabfassung für Revit 2027 herunterladen</>}
              </button>
            </p>

            <Schritte punkte={[
              'Auf dem Arbeitsplatz entpacken, Revit 2027 muss geschlossen sein.',
              'install-arbeitsplatz.ps1 als Administrator ausführen — es installiert '
              + 'nur nach %ProgramData%\\Autodesk\\Revit\\Addins\\2027\\.',
              'Revit 2027 starten - der Reiter TuV erscheint.',
            ]} />

            <p className="sehr-schwach" style={{ maxWidth: 680 }}>
              Der Aktualisierer hält nur die Fassung für Revit 2025 aktuell. Die
              Vorabfassung für 2027 bei Bedarf hier neu herunterladen.
            </p>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            DER AKTUALISIERER GEHÖRT HIERHER

            Wer sich das Add-in holt, soll im selben Zug sehen, dass es
            sich künftig selbst holt. Stünde er woanders, würde er
            übersehen — und dann bliebe das Add-in auf dem Stand des
            Tages, an dem es installiert wurde. ══════════════════════ */}
        {paket('aktualisierer')?.vorhanden && (
          <div style={{ marginTop: 22, paddingTop: 18,
                        borderTop: '1px solid var(--linie)' }}>
            <h3 style={{ marginTop: 0, fontSize: 15 }}>
              Damit es von selbst aktuell bleibt
            </h3>

            <p style={{ marginTop: 0, maxWidth: 680 }}>
              Der <strong>TuV-Addin-Aktualisierer</strong> läuft im
              Hintergrund, meldet sich stündlich und baut eine neue Fassung
              ein, sobald Revit beendet ist. Läuft Revit gerade, zeigt er die
              Änderungen und bittet um das Beenden — er beendet es nie selbst.
            </p>

            <Fassung paket={paket('aktualisierer')} />

            <p>
              <button
                className="m-knopf"
                disabled={laeuft === 'aktualisierer'}
                onClick={() => laden('/api/downloads/aktualisierer',
                                     'TuvAddinAktualisierer.zip',
                                     'aktualisierer')}
              >
                {laeuft === 'aktualisierer'
                  ? 'wird geladen …'
                  : <><IconHerunterladen size={16} />Herunterladen</>}
              </button>
            </p>

            <Schritte punkte={[
              'Die exe nach %LocalAppData%\\TuV\\AddinAktualisierer\\ kopieren.',
              'zertifikat-vertrauen.ps1 als Administrator ausführen — sonst '
              + 'fragt Revit bei JEDER neuen Fassung nach, ob das Add-in '
              + 'geladen werden soll.',
              'Die einstellungen.json mit dem Zugangsschlüssel nach '
              + '%AppData%\\TuV\\AddinAktualisierer\\ legen. Sie wird unter '
              + 'Maschinen → Arbeitsstationen erzeugt.',
              'Einmal starten. Er richtet den Autostart ein und holt die '
              + 'aktuelle Fassung.',
            ]} />

            <p className="sehr-schwach" style={{ maxWidth: 680 }}>
              In der Statusleiste erscheint ein Symbol. Ein Doppelklick darauf
              zeigt Fassung, Änderungen und den Zustand — grün aktiv, rot
              Fehler, grau untätig.
            </p>
          </div>
        )}
      </ContentSection>

      <ContentSection
        titel="Verarbeitungsmaschine einrichten"
        beschreibung={'Eine Windows-Maschine mit installiertem Revit, die Aufträge '
          + 'abarbeitet. Sie braucht beide Pakete.'}
      >

        <div style={{ display: 'grid', gap: 18, marginTop: 18 }}>
          <div>
            <strong>1. Agent</strong>
            <p className="schwach" style={{ margin: '4px 0 8px' }}>
              Holt Aufträge vom Server ab und startet Revit. Braucht einen
              eigenen Zugangsschlüssel je Maschine.
            </p>
            <Fassung paket={paket('agent')} />
        <Zustand vorhanden={paket('agent')?.vorhanden ?? false} />
            <p className="sehr-schwach" style={{ maxWidth: 680 }}>
              Das Paket wird unter <Link to="/maschinen">Maschinen</Link> heruntergeladen -
              mit eingetragenem Schlüssel, sobald die Maschine angelegt ist.
            </p>
          </div>

          <div>
            <strong>2. Revit-Erweiterung</strong>
            <p className="schwach" style={{ margin: '4px 0 8px' }}>
              Fuehrt den eigentlichen Export in Revit aus. Nur für
              Verarbeitungsmaschinen, nicht für Arbeitsplätze.
            </p>
            <Fassung paket={paket('appbundle')} />
        <Zustand vorhanden={paket('appbundle')?.vorhanden ?? false} />

            {paket('appbundle')?.vorhanden && (
              <p>
                <button
                  className="m-knopf m-knopf-haupt"
                  disabled={laeuft === 'appbundle'}
                  onClick={() => laden('/api/downloads/appbundle', 'TuvExportApp.zip', 'appbundle')}
                >
                  {laeuft === 'appbundle' ? 'wird geladen …' : <><IconHerunterladen size={16} />Herunterladen</>}
                </button>
              </p>
            )}
          </div>
        </div>

        <Schritte punkte={[
          'Unter Maschinen eine neue anlegen - der Schlüssel erscheint dabei einmalig.',
          'Agentenpaket mit Schlüssel herunterladen und entpacken.',
          'install-maschine.ps1 als Administrator ausführen - richtet Agent, '
            + 'Erweiterung und geplante Aufgabe in einem Zug ein.',
          'Revit einmal von Hand starten und die Autodesk-Anmeldung durchführen.',
        ]} />

        <div className="hinweis warnung" style={{ maxWidth: 680 }}>
          <span className="hinweis-zeichen">!</span>
          <div>
            Die geplante Aufgabe startet den Agenten bei der <strong>Benutzeranmeldung</strong>,
            nicht beim Systemstart - Revit braucht ein Benutzerprofil und laeuft nicht
            als Systemdienst. Damit die Maschine einen Neustart ohne Zutun uebersteht,
            ist die automatische Anmeldung einzurichten.
          </div>
        </div>
      </ContentSection>

      <ContentSection
        titel="Serveradresse"
        beschreibung="Ist in allen Paketen bereits eingetragen."
      >
        <PropertyGrid
          spalten={1}
          eintraege={[{ beschriftung: 'Serveradresse', wert: uebersicht.serveradresse }]}
        />
      </ContentSection>
    </>
  );
}

/**
 * Die Fassung und ihr Datum.
 *
 * Sie stehen hier, weil die Seite sonst bei jeder Fassung gleich aussieht.
 * Ein Mitarbeiter, der sich das Add-in holt, kann dann nicht erkennen, ob er
 * die neue hat — und wer es schon installiert hat, nicht, ob sich das erneute
 * Herunterladen lohnt. Genau daran blieb hier einmal eine alte Fassung
 * stehen, ohne dass es jemandem auffiel.
 */
function Fassung({ paket }: {
  paket?: { fassung?: string | null; stand?: string | null };
}) {
  if (!paket?.fassung) return null;

  const stand = paket.stand
    ? new Date(paket.stand).toLocaleDateString('de-DE', {
        day: '2-digit', month: '2-digit', year: 'numeric',
      })
    : null;

  return (
    <p className="schwach" style={{ fontSize: 13, margin: '2px 0 10px' }}>
      Fassung <strong>{paket.fassung}</strong>
      {stand && <> · freigegeben am {stand}</>}
    </p>
  );
}

function Zustand({ vorhanden }: { vorhanden: boolean }) {
  if (vorhanden) return null;

  return (
    <div className="hinweis warnung" style={{ maxWidth: 680 }}>
      <StatusBadge zustand="warnung" text="nicht hinterlegt" klein />
      <div>
        Noch nicht hinterlegt. Nach dem Bauen einspielen mit{' '}
        <code>deploy/scripts/release-hochladen.sh</code>
      </div>
    </div>
  );
}

function Schritte({ punkte }: { punkte: string[] }) {
  return (
    <ol style={{ paddingLeft: 20, maxWidth: 680, marginTop: 14 }}>
      {punkte.map((p, i) => (
        <li key={i} style={{ marginBottom: 6, fontSize: 13, color: 'var(--text-schwach)' }}>
          {p}
        </li>
      ))}
    </ol>
  );
}
